<script setup>
// ContactManageView.vue — tela de detalhe do contato (/contacts/:id).
//
// Refator: ClickUp Task Detail style. Substitui o layout 2-col com
// "tudo no mesmo lugar" por hero compacto sticky + tabs principais
// (Visao geral / Conversas / Notas / Atributos / Atividade) + sidebar
// slim a direita com cards About / Tags / Conversas mini.
//
// Mantem fluxos existentes: send message (ComposeConversation), block,
// merge dialog, delete confirm, update do form, fetch de inboxes/notas/
// conversas/atributos no mount.
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import ContactDetailHero from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailHero.vue';
import ContactDetailTabs from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailTabs.vue';
import ContactDetailSidebar from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailSidebar.vue';
import ContactDetailOverview from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailOverview.vue';
import ContactDetailActivity from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailActivity.vue';
import ContactDetailNotFound from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailNotFound.vue';
import ContactDetailSkeleton from 'dashboard/components-next/Contacts/ContactDetail/ContactDetailSkeleton.vue';

import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const TABS = {
  OVERVIEW: 'overview',
  CONVERSATIONS: 'conversations',
  NOTES: 'notes',
  ATTRIBUTES: 'attributes',
  ACTIVITY: 'activity',
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const contactGetter = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const conversationsByContact = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);

const activeTab = ref(TABS.OVERVIEW);
const confirmDeleteDialogRef = ref(null);
const mergeDialogRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() =>
  contactGetter.value(route.params.contactId)
);

const contactConversations = computed(
  () => conversationsByContact.value(route.params.contactId) || []
);

const notes = computed(
  () => notesByContact.value(route.params.contactId) || []
);

const showSkeleton = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const showNotFound = computed(
  () => !showSkeleton.value && !selectedContact.value
);

const tabs = computed(() => [
  {
    value: TABS.OVERVIEW,
    label: t('CONTACT_VIEW.TABS.OVERVIEW'),
    icon: 'i-lucide-user',
  },
  {
    value: TABS.CONVERSATIONS,
    label: t('CONTACT_VIEW.TABS.CONVERSATIONS'),
    icon: 'i-lucide-message-circle',
    count: contactConversations.value.length,
  },
  {
    value: TABS.NOTES,
    label: t('CONTACT_VIEW.TABS.NOTES'),
    icon: 'i-lucide-sticky-note',
    count: notes.value.length,
  },
  {
    value: TABS.ATTRIBUTES,
    label: t('CONTACT_VIEW.TABS.ATTRIBUTES'),
    icon: 'i-lucide-tag',
  },
  {
    value: TABS.ACTIVITY,
    label: t('CONTACT_VIEW.TABS.ACTIVITY'),
    icon: 'i-lucide-history',
  },
]);

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (!route.params.contactId) return;
  await store.dispatch('contacts/show', { id: route.params.contactId });
  await store.dispatch(
    'contacts/fetchContactableInbox',
    route.params.contactId
  );
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => store.dispatch('attributes/get');

const toggleBlock = async isBlocked => {
  const messages = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };
  try {
    await store.dispatch('contacts/update', {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(isBlocked ? messages.success.unblock : messages.success.block);
  } catch (error) {
    useAlert(isBlocked ? messages.error.unblock : messages.error.block);
  }
};

const handleAvatarUpload = async ({ file }) => {
  try {
    await store.dispatch('contacts/update', {
      ...selectedContact.value,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  if (!selectedContact.value?.id) return;
  try {
    await store.dispatch('contacts/deleteAvatar', selectedContact.value.id);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};

const openDeleteDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef?.open();
};

const openMergeDialog = () => {
  mergeDialogRef.value?.open();
};

const handleViewAllConversations = () => {
  activeTab.value = TABS.CONVERSATIONS;
};

// Quando o usuario chega na pagina e o ID muda (back/forward, deep link)
watch(
  () => route.params.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      activeTab.value = TABS.OVERVIEW;
      fetchActiveContact();
      fetchContactNotes();
      fetchContactConversations();
    }
  }
);

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-hidden bg-n-background">
    <ContactDetailSkeleton v-if="showSkeleton" />

    <ContactDetailNotFound
      v-else-if="showNotFound"
      @go-back="goToContactsList"
    />

    <div v-else class="flex flex-col flex-1 h-full min-h-0">
      <!-- Hero sticky -->
      <ContactDetailHero
        :contact="selectedContact"
        :conversations-count="contactConversations.length"
        :is-updating="isUpdatingContact"
        @toggle-block="toggleBlock"
        @avatar-upload="handleAvatarUpload"
        @avatar-delete="handleAvatarDelete"
        @merge="openMergeDialog"
        @delete="openDeleteDialog"
      />

      <!-- Tabs -->
      <ContactDetailTabs v-model="activeTab" :tabs="tabs" />

      <!-- Conteudo: main + sidebar slim -->
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <main class="flex-1 flex flex-col min-w-0 overflow-hidden">
          <ContactDetailOverview
            v-if="activeTab === TABS.OVERVIEW"
            :selected-contact="selectedContact"
          />
          <div
            v-else-if="activeTab === TABS.CONVERSATIONS"
            class="flex-1 overflow-y-auto"
          >
            <ContactHistory />
          </div>
          <div
            v-else-if="activeTab === TABS.NOTES"
            class="flex-1 overflow-y-auto"
          >
            <ContactNotes />
          </div>
          <div
            v-else-if="activeTab === TABS.ATTRIBUTES"
            class="flex-1 overflow-y-auto"
          >
            <ContactCustomAttributes :selected-contact="selectedContact" />
          </div>
          <ContactDetailActivity v-else-if="activeTab === TABS.ACTIVITY" />
        </main>

        <ContactDetailSidebar
          :contact="selectedContact"
          :conversations-count="contactConversations.length"
          @view-all-conversations="handleViewAllConversations"
        />
      </div>
    </div>

    <!-- Merge dialog: ContactMerge ja renderiza titulo/descricao/form -->
    <Dialog
      ref="mergeDialogRef"
      type="edit"
      width="lg"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <ContactMerge
        v-if="selectedContact"
        :selected-contact="selectedContact"
        class="!p-0"
        @go-to-contacts-list="
          () => {
            mergeDialogRef?.close();
            goToContactsList();
          }
        "
        @reset-tab="mergeDialogRef?.close()"
      />
    </Dialog>

    <ConfirmContactDeleteDialog
      ref="confirmDeleteDialogRef"
      :selected-contact="selectedContact"
      @go-to-contacts-list="goToContactsList"
    />
  </div>
</template>
